// Geometry primitives shared by every bracket builder. All units are PDF
// points (1/72 in) at final print size.
export const winnerName = (m) => {
  if (!m?.winner) return null;
  if (m.winner === m.team_a) return m.team_a_name;
  if (m.winner === m.team_b) return m.team_b_name;
  return null;
};

const ROUND_NAMES = ['FINAL', 'SEMIFINALS', 'QUARTERFINALS', 'ROUND OF 16', 'ROUND OF 32', 'ROUND OF 64', 'ROUND OF 128'];
export const roundName = (d) => ROUND_NAMES[d] || `ROUND ${d}`;

export function geo(M) {
  const g = { M, items: [] };
  const add = (it) => { g.items.push(it); return it; };
  g.colX = (c) => c * (M.cardW + M.colGap);
  g.rowsY = (c) => c.y + M.metaH + M.rowH;
  g.slotY = (c, s) => c.y + M.metaH + M.rowH * (s + 0.5);
  g.champH = Math.max(M.cardH, M.fs * 3.4);
  g.top = (y) => y + M.headerH + M.fs * 0.6 - (M.slotH - M.cardH) / 2;
  g.card = (match, col, cy, o = {}) => add({
    type: 'match', id: match.id, match, col, x: g.colX(col), y: cy - M.cardH / 2, w: M.cardW, h: M.cardH, tone: 'normal', ...o,
  });
  g.cardAtRows = (match, col, ry, o) => g.card(match, col, ry - M.metaH / 2, o);
  g.champion = (col, cy, o = {}) => add({
    type: 'champion', x: g.colX(col), y: cy - g.champH / 2, w: M.cardW, h: g.champH, title: 'CHAMPION', tone: 'gold', ...o,
  });
  g.line = (pts, o = {}) => add({ type: 'line', pts, ...o });
  g.header = (col, y, text, o = {}) => add({ type: 'header', x: g.colX(col), y, w: M.cardW, h: M.hfs * 1.8, text, tone: 'normal', ...o });
  g.note = (x, y, text, o = {}) => add({ type: 'note', x, y, text, size: M.fs * 0.6, anchor: 'start', ...o });
  g.connect = (child, parent, slot, dir, o = {}) => {
    const x0 = dir > 0 ? child.x + child.w : child.x;
    const x1 = dir > 0 ? parent.x : parent.x + parent.w;
    const xe = x1 - (dir * M.colGap) / 2;
    const y0 = g.rowsY(child);
    const y1 = g.slotY(parent, slot);
    return g.line([[x0, y0], [xe, y0], [xe, y1], [x1, y1]], {
      done: !!child.match?.winner, pg: child.pg, tone: child.tone === 'lb' ? 'lb' : 'normal', ...o,
    });
  };
  g.stub = (parent, slot, dir, text) => {
    const x1 = dir > 0 ? parent.x : parent.x + parent.w;
    const xe = x1 - dir * M.colGap * 0.6;
    const y = g.slotY(parent, slot);
    g.line([[xe, y], [x1, y]], { stub: true, pg: parent.pg });
    if (text) g.note(xe, y - M.fs * 0.28, text, { anchor: dir > 0 ? 'start' : 'end', size: M.fs * 0.48, pg: parent.pg, muted: true });
  };
  return g;
}

export function placeUnits(g, units, { colOf, top, dir = 1, pgOf = () => 'C', toneOf = () => 'normal', byeText = null }) {
  const cards = new Map();
  units.forEach(({ node, y }, id) => {
    cards.set(id, g.card(node.match, colOf(node.match), top + y * g.M.slotH, { pg: pgOf(id), tone: toneOf(id) }));
  });
  units.forEach(({ node }, id) => {
    if (!node.kids.some(Boolean)) return;
    const parent = cards.get(id);
    node.kids.forEach((k, s) => (k
      ? g.connect(cards.get(k.match.id), parent, s, dir, { dashed: k.via === 'L' })
      : g.stub(parent, s, dir, byeText)));
  });
  return cards;
}

const bottomOf = (items) => Math.max(...items.map((it) => (it.pts ? Math.max(...it.pts.map((p) => p[1])) : it.y + (it.h || 0))));

export function placeExtras(g, extras, pg) {
  if (!extras?.length) return;
  const { M } = g;
  const mine = g.items.filter((it) => it.type === 'match' && (pg === 'C' || it.pg === pg));
  const col0 = mine.length ? Math.min(...mine.map((c) => c.col)) : 0;
  const y0 = bottomOf(g.items) + M.fs * 1.6;
  g.note(g.colX(col0), y0 + M.hfs, 'OTHER GAMES', { size: M.hfs, bold: true, pg });
  extras.forEach((m, i) => g.card(m, col0 + (i % 5), y0 + M.hfs * 1.8 + Math.floor(i / 5) * M.slotH + M.cardH / 2, { pg, tone: 'placement' }));
}

function bbox(items) {
  let x0 = Infinity; let y0 = Infinity; let x1 = -Infinity; let y1 = -Infinity;
  const take = (x, y) => { x0 = Math.min(x0, x); x1 = Math.max(x1, x); y0 = Math.min(y0, y); y1 = Math.max(y1, y); };
  items.forEach((it) => {
    if (it.pts) it.pts.forEach(([x, y]) => take(x, y));
    else if (it.type === 'note') {
      const w = it.text.length * it.size * 0.5;
      const xs = it.anchor === 'end' ? it.x - w : it.x;
      take(xs, it.y - it.size);
      take(xs + w, it.y + it.size * 0.3);
    } else { take(it.x, it.y); take(it.x + it.w, it.y + it.h); }
  });
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

export function finalize(g, groups) {
  const pad = Math.max(1.5, g.M.fs * 0.3);
  const viewports = (groups || [null]).map((gr) => {
    const b = bbox(g.items.filter((it) => !gr || gr.includes(it.pg || 'C')));
    return { x: b.x - pad, y: b.y - pad, w: b.w + 2 * pad, h: b.h + 2 * pad, groups: gr };
  });
  return { items: g.items, viewports };
}
